import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { getConversionLabel, streamLabel } from "@/lib/wricef-status";
import {
  WRICEF_COLORS,
  WRICEF_NAMES,
  type WricefCatalogItem,
  type WricefWorklistResponse,
} from "@/types/wricef";

import { CatalogPager } from "./CatalogPager";
import { worklistSections } from "./cleanCoreData";
import {
  cleanCoreLevelMeta,
  EXTENSIBILITY_META,
  LEVEL_BAR,
} from "./cleanCoreMeta";

/** Badge sólido C/D — o nível diz a urgência da remediação. */
function LevelBadge({ item }: { item: WricefCatalogItem }) {
  const cc = cleanCoreLevelMeta(item.clean_core_level);
  if (!cc.level) return null;
  const bar = item.clean_core_level ? LEVEL_BAR[item.clean_core_level] : "";
  return (
    <span
      className={`inline-flex h-5 w-5 items-center justify-center rounded font-mono text-[10px] font-bold text-white ${bar}`}
      title={cc.description}
    >
      {cc.label}
    </span>
  );
}

/** O "porquê" técnico: conversão (sinal -> alvo) + modelo de extensão. */
function Why({ item }: { item: WricefCatalogItem }) {
  const { t } = useTranslation();
  const conv = item.conversion_tipo
    ? getConversionLabel(item.conversion_tipo)
    : null;
  const target = item.extensibility_target
    ? EXTENSIBILITY_META[item.extensibility_target]
    : null;

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
        {t("Why remediate")} · {streamLabel(item.resolved_stream)}
      </span>
      {conv ? (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded border bg-muted px-2 py-0.5 font-mono text-[11px]">
            {WRICEF_NAMES[item.wricef]}
          </span>
          <ArrowRight className="h-3 w-3 text-red-600" />
          <span className="rounded border bg-muted px-2 py-0.5 font-mono text-[11px]">
            {conv}
          </span>
        </div>
      ) : (
        <span className="text-xs text-muted-foreground">
          {t("No conversion type classified — depends on gap analysis.")}
        </span>
      )}
      {item.conversion_descricao && (
        <p className="text-[11px] leading-snug text-muted-foreground">
          {item.conversion_descricao}
        </p>
      )}
      {target && (
        <p className="text-[11px] text-muted-foreground">
          Recommended model:{" "}
          <span className="font-semibold text-foreground">{target.label}</span>{" "}
          — {target.hint}
        </p>
      )}
    </div>
  );
}

function WorklistCard({ item }: { item: WricefCatalogItem }) {
  const target = item.extensibility_target
    ? EXTENSIBILITY_META[item.extensibility_target]
    : null;
  const critPct =
    item.criticality_score != null
      ? Math.round(item.criticality_score * 100)
      : null;
  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-bold text-primary">
          {item.obj_name} · {item.obj_type_tadir}
        </span>
        <Badge
          variant="outline"
          className={`text-[10px] ${WRICEF_COLORS[item.wricef]}`}
          title={WRICEF_NAMES[item.wricef]}
        >
          {item.wricef}
        </Badge>
        <LevelBadge item={item} />
        {target && (
          <Badge variant="outline" className={`text-[10px] ${target.badge}`}>
            {target.label}
          </Badge>
        )}
        <span className="ml-auto font-mono text-[10px] font-bold text-red-600">
          {critPct != null ? `criticality ${critPct}/100` : "no score"}
        </span>
      </div>
      {item.description_pt && (
        <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">
          {item.description_pt}
        </p>
      )}
      <Why item={item} />
    </div>
  );
}

/**
 * A worklist de Decisions & Remediation, uma página por vez.
 *
 * O que entra e em que ordem é decidido no banco (`/wricef-catalog/worklist`).
 * A aba recebia as 1.000 primeiras linhas do catálogo em ordem alfabética e
 * filtrava no navegador; num catálogo de 7.288 objetos, "os N que precisam de
 * decisão" era a conta de um recorte.
 */
export function WorklistView({
  data,
  selectedStreams,
  onToggleStream,
  onClearStreams,
  page,
  pageSize,
  onPageChange,
}: {
  data: WricefWorklistResponse;
  selectedStreams: Set<string>;
  onToggleStream: (stream: string) => void;
  onClearStreams: () => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation();
  const sections = useMemo(
    () => worklistSections(data.items, data.groups, data.offset, selectedStreams),
    [data, selectedStreams],
  );
  const overall = data.groups.reduce((acc, g) => acc + g.count, 0);

  if (overall === 0) {
    return (
      <div className="rounded-lg border bg-white p-6 text-center text-xs text-muted-foreground">
        {t("No C/D (remediation) or high-criticality object in this catalog — nothing that needs an extension decision. The cloud-ready A/B levels stay in the WRICEF Catalog.")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="text-xs text-muted-foreground">
        The{" "}
        <span className="font-semibold text-foreground">
          {data.total.toLocaleString("en-US")}
        </span>{" "}
        {selectedStreams.size > 0 && `of ${overall.toLocaleString("en-US")} `}
        objects that need a decision — C/D (remediation) or high criticality —
        with the technical signal and the S/4 target up front. Grouped by module
        (remediation density), ordered by criticality. The low-impact cloud-ready
        A/B stay in the Catalog, without competing for attention.
      </p>

      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          onClick={onClearStreams}
          aria-pressed={selectedStreams.size === 0}
          className={`text-[11px] px-1.5 py-0.5 rounded border ${
            selectedStreams.size === 0
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background text-muted-foreground border-input hover:bg-muted"
          }`}
        >
          {t("All modules")}
        </button>
        {data.groups.map((g) => {
          const active = selectedStreams.has(g.stream);
          return (
            <button
              key={g.stream}
              type="button"
              data-testid="worklist-module-chip"
              aria-pressed={active}
              onClick={() => onToggleStream(g.stream)}
              className={`text-[11px] px-1.5 py-0.5 rounded border tabular-nums ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground border-input hover:bg-muted"
              }`}
            >
              {streamLabel(g.stream, data.stream_labels)}{" "}
              <span className="opacity-70">({g.count.toLocaleString("en-US")})</span>
            </button>
          );
        })}
      </div>

      {/* Em cima e embaixo: embaixo, o botão flutuante do Chat cobre o Next
          quando a lista termina no rodapé da tela. */}
      <CatalogPager
        page={page}
        pageSize={pageSize}
        total={data.total}
        onPageChange={onPageChange}
      />

      {sections.length === 0 ? (
        <div className="rounded-lg border bg-white p-6 text-center text-xs text-muted-foreground">
          {t("No objects for the selected modules.")}
        </div>
      ) : (
        sections.map((sec) => (
          <div key={sec.stream} className="flex flex-col gap-2.5">
            <div className="flex items-baseline gap-2">
              <h3 className="text-sm font-bold">
                {streamLabel(sec.stream, data.stream_labels)}
              </h3>
              <span className="font-mono text-[11px] font-bold text-red-600">
                {sec.total.toLocaleString("en-US")}
              </span>
              {sec.continued && (
                <span className="text-[11px] text-muted-foreground">
                  (continued from the previous page)
                </span>
              )}
            </div>
            {sec.items.map((item) => (
              <WorklistCard key={item.id} item={item} />
            ))}
          </div>
        ))
      )}

      <CatalogPager
        page={page}
        pageSize={pageSize}
        total={data.total}
        onPageChange={onPageChange}
      />
    </div>
  );
}
